import { artifactFilename, dashboardIdOf } from '@/lib/artifacts';
import { withReferences } from '@/lib/dashboardReferences';
import { downloadFromUrl, saveBlob } from '@/lib/download';
import { ChevronLeft, ChevronRight, Download, Maximize2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSetRecoilState } from 'recoil';

import type { IMessageElement } from '@chainlit/react-client';

import { ArtifactFullView } from '@/components/ArtifactFullView';
import { DashboardFrame } from '@/components/Dashboards/DashboardFrame';
import { useDashboardTitles } from '@/components/Dashboards/useDashboardTitles';
import { Button } from '@/components/ui/button';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink
} from '@/components/ui/pagination';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { queuedPromptState } from '@/state/chat';

import {
    DM_RIGHT_SIDEBAR_DEFAULT_WIDTH,
    useDMRightSidebar
} from '../dm-right-sidebar';
import type { PanelContentProps } from '../types';
import { ScaledArtifactFrame } from './ScaledArtifactFrame';

const ARTIFACT_SANDBOX = 'allow-scripts allow-same-origin allow-downloads';
const ARTIFACT_SIDEBAR_WIDTH_FRACTION = 0.5;
const COLLAPSED_RAIL_WIDTH = 65;

interface ArtifactProps {
    url?: string;
    filename?: string;
    mime?: string;
    html?: string;
}

interface ArtifactsPanelProps extends PanelContentProps {
    selectedId: string | null;
    onSelect: (id: string) => void;
}

const artifactProps = (element: IMessageElement): ArtifactProps =>
    (element as any).props ?? {};

async function downloadArtifact({ url, filename, mime, html }: ArtifactProps) {
    const name = filename || 'artifact';

    if (html) {
        saveBlob(new Blob([html], { type: mime || 'text/html' }), name);
        return;
    }

    if (url) await downloadFromUrl(url, name);
}

function openArtifactInNewTab(html: string) {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank', 'noopener');
}

function useHeaderSlot(id: string) {
    const [slot, setSlot] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setSlot(document.getElementById(id));
    }, [id]);
    return slot;
}

function useWideSidebar() {
    const { sidebarWidth, resizeSidebar, isMobile } = useDMRightSidebar();
    const restoreWidth = useRef(sidebarWidth || DM_RIGHT_SIDEBAR_DEFAULT_WIDTH);

    useEffect(() => {
        if (isMobile) return;
        const original = restoreWidth.current;
        resizeSidebar(
            Math.round(
                window.innerWidth * ARTIFACT_SIDEBAR_WIDTH_FRACTION -
                    COLLAPSED_RAIL_WIDTH
            )
        );
        return () => resizeSidebar(original);
    }, [isMobile]);
}

export function ArtifactsPanel({
    elements,
    selectedId,
    onSelect
}: ArtifactsPanelProps) {
    useWideSidebar();
    const titleSlot = useHeaderSlot('sidebar-header-title-slot');
    const centerSlot = useHeaderSlot('sidebar-header-center-slot');
    const actionsSlot = useHeaderSlot('sidebar-header-actions-slot');
    const [fullViewOpen, setFullViewOpen] = useState(false);
    const hasDashboard = elements.some((element) => dashboardIdOf(element));
    const dashboardTitles = useDashboardTitles(hasDashboard);
    const setQueuedPrompt = useSetRecoilState(queuedPromptState);

    if (elements.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center gap-y-1 py-8 text-center">
                <p className="text-secondary-foreground text-base">
                    No artifacts yet
                </p>
                <p className="text-muted-foreground text-xs">
                    HTML files the agent creates will appear here
                </p>
            </div>
        );
    }

    const selectedIndex = Math.max(
        0,
        elements.findIndex((el) => el.id === selectedId)
    );
    const activeIndex = selectedId ? selectedIndex : elements.length - 1;
    const selected = elements[activeIndex];
    const artifact = artifactProps(selected);
    const dashboardId = dashboardIdOf(selected);
    const displayName =
        (dashboardId && dashboardTitles[dashboardId]) ||
        artifactFilename(selected);
    const hasMultiple = elements.length > 1;

    const showRelative = (offset: number) =>
        onSelect(
            elements[(activeIndex + offset + elements.length) % elements.length]
                .id
        );

    return (
        <div className="h-full min-h-0">
            {centerSlot &&
                createPortal(
                    <div className="flex flex-col items-center">
                        {hasMultiple && (
                            <Pagination className="mx-0 w-auto">
                                <PaginationContent className="gap-2">
                                    <PaginationItem>
                                        <PaginationLink
                                            href="#"
                                            aria-label="Previous artifact"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                showRelative(-1);
                                            }}
                                        >
                                            <ChevronLeft className="size-5!" />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem className="min-w-12 text-center text-sm tabular-nums">
                                        {activeIndex + 1} / {elements.length}
                                    </PaginationItem>
                                    <PaginationItem>
                                        <PaginationLink
                                            href="#"
                                            aria-label="Next artifact"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                showRelative(1);
                                            }}
                                        >
                                            <ChevronRight className="size-5!" />
                                        </PaginationLink>
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        )}
                    </div>,
                    centerSlot
                )}
            {titleSlot &&
                createPortal(
                    <>
                        <span className="bg-border w-px h-5" aria-hidden />
                        <span
                            className="max-w-56 font-mono text-muted-foreground text-xs truncate"
                            title={displayName}
                        >
                            {displayName}
                        </span>
                    </>,
                    titleSlot
                )}
            {actionsSlot &&
                createPortal(
                    <div className="flex items-center gap-1">
                        {!dashboardId && artifact.html && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setFullViewOpen(true)}
                                        aria-label="Open full view"
                                    >
                                        <Maximize2 />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Full view</TooltipContent>
                            </Tooltip>
                        )}
                        {!dashboardId && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                            downloadArtifact(artifact)
                                        }
                                        disabled={
                                            !artifact.url && !artifact.html
                                        }
                                        aria-label="Download"
                                    >
                                        <Download />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Download</TooltipContent>
                            </Tooltip>
                        )}
                    </div>,
                    actionsSlot
                )}
            {dashboardId && (
                <div className="bg-background border border-border rounded-lg h-full overflow-hidden">
                    <DashboardFrame
                        key={selected.id}
                        dashboardId={dashboardId}
                        onComment={(reference, text) =>
                            setQueuedPrompt(withReferences(text, [reference]))
                        }
                    />
                </div>
            )}
            {!dashboardId && artifact.html && (
                <div className="bg-background border border-border rounded-lg h-full overflow-hidden">
                    <ScaledArtifactFrame
                        html={artifact.html}
                        sandbox={ARTIFACT_SANDBOX}
                        title={artifact.filename || 'Artifact'}
                    />
                </div>
            )}
            {fullViewOpen && artifact.html && (
                <ArtifactFullView
                    title={artifact.filename || 'Artifact'}
                    html={artifact.html}
                    frameKey={selected.id}
                    sandbox={ARTIFACT_SANDBOX}
                    count={elements.length}
                    activeIndex={activeIndex}
                    onSelect={(index) => onSelect(elements[index].id)}
                    onClose={() => setFullViewOpen(false)}
                    onDownload={() => downloadArtifact(artifact)}
                    onOpenInNewTab={() => openArtifactInNewTab(artifact.html!)}
                />
            )}
        </div>
    );
}
